import { FC } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, Pencil } from '@factorialco/f0-react/icons/app'
import { PerformanceGoalsMetricGroupCrumbQuery } from 'generated/resources/graphql'

import { checkFeatures } from 'modules/core/hooks/useFeatures'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { formatBackendDateRange } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useDeleteMetricGoal } from 'modules/goals/hooks/deleteMetricGroup'

function buildDescription(
  description: string | undefined | null,
  performanceCycle: { startOn?: string; endOn?: string; title?: string } | undefined | null,
  isRevamp: boolean
): string {
  const base = description || ''

  if (isRevamp || !performanceCycle?.startOn || !performanceCycle?.endOn) {
    return base
  }

  const dateRange = formatBackendDateRange(performanceCycle.startOn, performanceCycle.endOn)
  return base ? `${base} · ${dateRange}` : dateRange
}

type MetadataItem = {
  label: string
  hideLabel: boolean
  value:
    | { type: 'text'; content: string }
    | { type: 'status'; label: string; variant: 'positive' | 'warning' }
}

function buildRevampMetadata(
  needsAttention: boolean,
  performanceCycle: { startOn?: string; endOn?: string } | undefined | null
): MetadataItem[] | undefined {
  const statusItem: MetadataItem | false = needsAttention
    ? {
        label: i18n.t('goals_revamp.detail.status_label'),
        hideLabel: true,
        value: {
          type: 'status',
          label: i18n.t('performance_goals.metric_assessment_status_tag.needs_attention'),
          variant: 'warning',
        },
      }
    : !!performanceCycle && {
        label: i18n.t('goals_revamp.detail.status_label'),
        hideLabel: true,
        value: {
          type: 'status',
          label: i18n.t('goals_revamp.detail.status_in_progress'),
          variant: 'positive',
        },
      }

  const items: (MetadataItem | false)[] = [
    !!(performanceCycle?.startOn && performanceCycle?.endOn) && {
      label: i18n.t('performance_cycles.cycle_filter.label'),
      hideLabel: true,
      value: {
        type: 'text' as const,
        content: formatBackendDateRange(performanceCycle.startOn, performanceCycle.endOn),
      },
    },
    statusItem,
  ]

  const filtered = items.filter((item): item is MetadataItem => !!item)
  return filtered.length > 0 ? filtered : undefined
}

type Props = {
  metricGroup: PerformanceGoalsMetricGroupCrumbQuery['goals']['metricGroup']
  features?: Array<{ name: string; enabled: boolean }>
}

export const GoalHeader: FC<Props> = ({ metricGroup, features }) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [_searchParams, setSearchParams] = useSearchParams()
  const { has_dev_goals_revamp } = checkFeatures(['dev_goals_revamp'], features)

  // TODO once we remove ResponsivePage's header, move CreateMetricModal and UpdateMetricGroupModal from MetricGroupPage to this component
  const openCreateDialog = () => setSearchParams({ modal: 'create' })
  const openEditDialog = () => setSearchParams({ modal: 'edit' })
  const { handleDelete } = useDeleteMetricGoal()

  const onDelete = async () => {
    if (!metricGroup) return

    await handleDelete(metricGroup, () => {
      navigate({
        pathname: navigation.goals.groups.fullPath(),
      })
    })
  }

  const performanceCycle = metricGroup?.performanceCycles?.[0]
  const canUpdateGoal = metricGroup?._policies?.update.can
  const canCreateGoalMetric = metricGroup?._policies?.createMetric.can
  const canDeleteGoal = metricGroup?._policies?.delete.can

  const summary = metricGroup?.summary
  const needsAttention = summary?.needsAttention ?? false

  const statusConfig = has_dev_goals_revamp
    ? undefined
    : performanceCycle
      ? {
          label: i18n.t('performance_cycles.cycle_filter.label'),
          text: performanceCycle.title,
          variant: 'neutral' as const,
        }
      : undefined

  const metadata = has_dev_goals_revamp
    ? buildRevampMetadata(needsAttention, performanceCycle)
    : undefined

  return (
    <ResourceHeader
      title={metricGroup?.title || ''}
      description={buildDescription(
        metricGroup?.description,
        performanceCycle,
        has_dev_goals_revamp
      )}
      status={statusConfig}
      metadata={metadata}
      primaryAction={
        canCreateGoalMetric
          ? {
              label: i18n.t('performance_goals.pages.metric_group_page.create_key_result_button'),
              icon: Add,
              onClick: openCreateDialog,
            }
          : undefined
      }
      otherActions={[
        canUpdateGoal && {
          label: i18n.t('common.edit'),
          icon: Pencil,
          onClick: openEditDialog,
        },
        canDeleteGoal && {
          label: i18n.t('common.delete'),
          variant: 'critical',
          icon: Delete,
          onClick: onDelete,
        },
      ].filter(Boolean)}
    />
  )
}
